import React from "react";

export type SeoHeadProps = {
  title: string;
  description?: string;
  canonicalPath?: string; // e.g. "/content/can/modern-wealth-playbook"
  image?: string; // absolute URL or path starting with /
};

export const SeoHead: React.FC<SeoHeadProps> = ({ title, description, canonicalPath, image }) => {
  React.useEffect(() => {
    document.title = title;

    const ensureTag = (name: string, attr: string, value: string) => {
      let el = document.querySelector(`${name}[${attr}="${value}"]`) as HTMLMetaElement | HTMLLinkElement | null;
      if (!el) {
        el = document.createElement(name) as any;
        document.head.appendChild(el);
      }
      (el as any)[attr] = value;
      return el;
    };

    if (description) {
      ensureTag('meta', 'name', 'description').setAttribute('content', description);
      ensureTag('meta', 'property', 'og:description').setAttribute('content', description);
      ensureTag('meta', 'name', 'twitter:description').setAttribute('content', description);
    }

    const base = import.meta.env.VITE_SITE_URL || window.location.origin;
    const canonical = canonicalPath ? `${base}${canonicalPath}` : window.location.href;
    const imagePath = image ?? (import.meta.env.VITE_DEFAULT_OG_PATH || '/og-image.png');
    const imageAbs = imagePath.startsWith('http') ? imagePath : `${base}${imagePath}`;

    // Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonical;

    // OpenGraph + Twitter
    ensureTag('meta', 'property', 'og:title').setAttribute('content', title);
    ensureTag('meta', 'property', 'og:url').setAttribute('content', canonical);
    ensureTag('meta', 'name', 'twitter:title').setAttribute('content', title);
    ensureTag('meta', 'name', 'twitter:card').setAttribute('content', 'summary_large_image');
    ensureTag('meta', 'property', 'og:image').setAttribute('content', imageAbs);
    ensureTag('meta', 'name', 'twitter:image').setAttribute('content', imageAbs);
  }, [title, description, canonicalPath, image]);

  return null;
};

export default SeoHead;
